// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT || '3500';
  const isDev = process.env.NODE_ENV === 'development';

  // In development, the dev API server is running in a different port (default 3500)
  if (isDev) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  // For SSR compatibility, check if window exists before accessing window.location
  if (marketplaceRootURL) {
    return marketplaceRootURL.replace(/\/$/, '');
  }
  
  // Only access window in browser environment (not during SSR)
  return typeof window !== 'undefined' ? window.location.origin : '';
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
export const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};


export const get = (path, params = {}) => {
  let url = `${apiBaseUrl()}${path}`;

  const queryString = Object.keys(params)
    .filter(key => params[key] !== undefined)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');

  // Append the query string if there are parameters
  if (queryString) {
    url += `?${queryString}`;
  }

  const options = {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Fetch Telia user information from session
//
// This endpoint retrieves the detailed identity verification data
// for users who have authenticated through Telia Tunnistus.
//
// See `server/api/telia-user-info.js` for the implementation.
export const teliaUserInfo = () => {
  return get('/api/telia-user-info');
};

// Save Telia verification data to user profile
//
// This endpoint moves Telia verification data from temporary session
// storage to permanent user profile storage.
//
// See `server/api/save-telia-verification.js` for the implementation.
export const saveTeliaVerification = () => {
  const url = `${apiBaseUrl()}/api/save-telia-verification`;
  
  // Use plain fetch with no body and no transit content type
  // since the endpoint only reads from session, not request body
  return window.fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

// Claim a company profile
//
// This endpoint verifies signing rights with Asiakastieto and links
// the company profile to the authenticated user.
//
// See `server/api/claim-company.js` for the implementation.
export const claimCompany = listingId => {
  const url = `${apiBaseUrl()}/api/claim-company`;
  
  return window.fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ listingId })
  }).then(res => {
    return res.json().then(data => {
      if (res.status >= 400) {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      }
      return data;
    });
  });
};

// Create a company profile for a company not yet in the marketplace.
//
// Verifies signing rights with Asiakastieto. Resolves to one of:
//   { status: 'published' }      - verified, profile created & live
//   { status: 'pending_review' } - could not auto-verify, submitted to the owner
//   { status: 'needs_name' }     - need the user to confirm the company name; resubmit with `name`
// or throws for { error: 'no_signing_rights' } (403) / { error: 'company_exists' } (409).
//
// See `server/api/create-company-profile.js` for the implementation.
export const createCompanyProfile = (ytunnus, name) => {
  const url = `${apiBaseUrl()}/api/create-company-profile`;

  return window.fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ytunnus, fallbackName: name }),
  }).then(res => {
    return res.json().then(data => {
      if (res.status >= 400) {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      }
      return data;
    });
  });
};

// TASK024: Update user credentials (email + password)
// See `server/api/update-credentials.js` for the implementation.
export const updateCredentials = (email, newPassword) => {
  const url = `${apiBaseUrl()}/api/update-credentials`;
  
  return window.fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, newPassword })
  }).then(res => {
    return res.json().then(data => {
      if (res.status >= 400) {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      }
      return data;
    });
  });
};

